import * as models from 'smg/admin/models';
import Autocomplete from 'smg/autocomplete';
import axios from 'smg/axios';
import * as html from 'smg/html';

export interface PromotionalHandle {
    // Forward-looking snapshot the future submit handler will fold into the
    // /orders POST body. Empty fields mean no code is applied.
    snapshot(): PromotionalDraft;
}

export interface PromotionalDraft {
    promotionalCodeID: string;
    code: string;
}

export interface PromotionalDeps {
    // The realm's network ID. Empty string when no realm has been picked —
    // the autocomplete returns nothing in that case so we don't fan out a
    // network-less query that would search every promotional code we have.
    getNetworkID?: () => string;
    // The selected realm/company ID. Some promos are scoped to a specific
    // realm rather than the whole network; smg.GetPromotionalCode accepts
    // NetworkID OR CompanyID, so we mirror that in the search query to
    // avoid flagging realm-scoped codes as invalid.
    getCompanyID?: () => string;
    // The currently selected customer's userID. Used to exclude their own
    // referral codes from suggestions — an admin placing on behalf of a
    // customer shouldn't be redeeming that customer's own code.
    getCustomerUserID?: () => string;
    // Fires whenever the resolved applied code changes (post-debounce). Empty
    // string means no code is applied. The page wires this into summary's
    // setPromoCode so the discount preview tracks live with input.
    onPromoChange?: (code: string) => void;
}

interface appliedPromo {
    id: string;
    code: string;
    name: string;
    description: string;
}

function toApplied(p: models.HyejooPromotionalCode, fallbackCode: string): appliedPromo | undefined {
    if (!p.id) {
        return undefined;
    }
    return {
        id: p.id,
        code: p.code || fallbackCode,
        name: p.name ?? '',
        description: p.description ?? '',
    };
}

function buildSearchQuery(text: string, networkID: string, companyID: string, customerUserID: string): string {
    // Always drop codes whose Expires date has already passed. Hyejoo's
    // negation matches "field is not null and field != X" — codes with no
    // expiry (NULL) would be dropped by a bare `expires:>=now`, so combine
    // it with `-has:expires` for the never-expires case.
    const parts = ['active:yes', '(expires:>=now or -has:expires)'];
    // Match smg.GetPromotionalCode's `NetworkID = X OR CompanyID = Y` —
    // some promos are realm-scoped rather than network-wide, and they'd be
    // marked invalid by a network-only filter.
    if (companyID) {
        parts.push(`(network:${networkID} or companyID:${companyID})`);
    } else {
        parts.push(`network:${networkID}`);
    }
    const trimmed = text.trim();
    if (trimmed) {
        // Backtick-quoted value lets Hyejoo treat the contents as a single
        // search token; trailing `*` is the prefix-match operator.
        parts.push(`code:\`${trimmed}*\``);
        if (customerUserID) {
            // Same NULL-handling concern as expires: `-ownerUserID:X` drops
            // rows where OwnerUserID IS NULL too. Generic (unowned) network
            // codes need to stay visible — wrap in OR with `-has:ownerUserID`.
            parts.push(`(-ownerUserID:${customerUserID} or -has:ownerUserID)`);
        }
    } else {
        // Empty input: only show generic (no owner) promos so the admin can
        // browse what's available without seeing other customers' referrals.
        parts.push('-has:ownerUserID');
    }
    return parts.join(' ');
}

export function SetupPromotional(deps?: PromotionalDeps): PromotionalHandle {
    const $input = document.getElementById('promo-code-input') as HTMLInputElement;
    const $clearBtn = document.getElementById('promo-code-clear') as HTMLButtonElement;
    const $success = document.getElementById('promo-code-success') as HTMLElement;
    const $successTitle = document.getElementById('promo-code-success-title') as HTMLElement;
    const $successDescription = document.getElementById('promo-code-success-description') as HTMLElement;

    let applied: appliedPromo | undefined;

    function applyPromo(p: appliedPromo) {
        // Same id and same code = same state; nothing to do. Skip the DOM
        // writes and the onPromoChange notify so the summary doesn't refetch
        // unnecessarily on every keystroke that resolves to the same promo.
        if (applied?.id === p.id && applied.code === p.code) {
            return;
        }
        applied = p;
        $successTitle.textContent = p.name || `Promo code: ${p.code}`;
        $successDescription.textContent = p.description;
        $successDescription.classList.toggle('d-none', !p.description);
        $success.classList.remove('d-none');
        $input.setCustomValidity('');
        // 'clearinvalid' is the signal ReportValidity registers a listener
        // for — dispatching it strips the `.is-invalid` class and removes the
        // injected invalid-feedback node. setCustomValidity('') alone doesn't
        // do that; the class only clears on the next input/change event,
        // which never fires when an autocomplete pick auto-applies.
        $input.dispatchEvent(new Event('clearinvalid'));
        deps?.onPromoChange?.(p.code);
    }

    function clearPromo(invalid: boolean) {
        const wasApplied = applied !== undefined;
        applied = undefined;
        $success.classList.add('d-none');
        if (invalid) {
            // Free-typed code with no exact match in the result set. Surface
            // it through the form's native validation so ReportValidity at
            // submit time blocks the order with a Bootstrap-styled message.
            $input.setCustomValidity('Not a valid code.');
        } else {
            $input.setCustomValidity('');
            $input.dispatchEvent(new Event('clearinvalid'));
        }
        if (wasApplied) {
            deps?.onPromoChange?.('');
        }
    }

    async function fetchPromos(text: string): Promise<models.HyejooPromotionalCode[]> {
        const networkID = deps?.getNetworkID?.() ?? '';
        if (!networkID) {
            // No realm picked — can't validate against a network. Treat as
            // "nothing applied" rather than "invalid".
            clearPromo(false);
            return [];
        }
        const customerUserID = deps?.getCustomerUserID?.() ?? '';
        const companyID = deps?.getCompanyID?.() ?? '';
        const resp = await axios.get('/v2/promotional-codes', {
            params: {
                q: buildSearchQuery(text, networkID, companyID, customerUserID),
                data: 'id,code,name,description,expires',
                max: 20,
                sort: 'code asc',
            },
        });
        const items = models.HyejooPromotionalCode.fromKeyArray(resp.data, 'results');

        // Auto-apply: if the input's current value exactly matches one of the
        // returned codes (case-insensitive), treat it as applied. Otherwise
        // clear any prior applied state and mark as invalid (for non-empty
        // text) so form submission catches it.
        const trimmed = text.trim();
        if (!trimmed) {
            clearPromo(false);
        } else {
            const lower = trimmed.toLowerCase();
            const exact = items.find(p => (p.code ?? '').toLowerCase() === lower);
            const a = exact ? toApplied(exact, trimmed) : undefined;
            if (a) {
                applyPromo(a);
            } else {
                clearPromo(true);
            }
        }

        return items;
    }

    new Autocomplete<models.HyejooPromotionalCode>({
        input: $input,
        get: v => fetchPromos(v),
        draw: p => {
            const code = html.Escape(p.code ?? '');
            const name = html.Escape(p.name ?? '');
            const description = html.Escape(p.description ?? '');
            return html.FromString(`<div class="lh-sm">
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                    <code class="fw-semibold">${code}</code>
                    ${name ? `<span class="small">${name}</span>` : ''}
                </div>
                ${description ? `<div class="small text-muted">${description}</div>` : ''}
            </div>`).firstElementChild as HTMLElement;
        },
        value: p => p.code ?? '',
        change: p => {
            // Autocomplete picks set the input value and dispatch 'input'
            // before this callback. The input event blocks autocomplete's
            // own debounce (via #selecting), so no fresh fetchPromos runs —
            // we have to apply directly here from the picked item.
            const a = toApplied(p, p.code ?? '');
            if (a) {
                applyPromo(a);
            }
        },
        allowEmptySearch: true,
        // Don't auto-pick the first result on Enter — the admin presses Enter
        // to commit exactly what they typed (which fetchPromos auto-applies
        // when it matches). Arrowing into a suggestion + Enter still picks
        // via autocomplete's own handler.
        noDefault: true,
    });

    $clearBtn.addEventListener('click', () => {
        $input.value = '';
        clearPromo(false);
        // Dispatch input so the autocomplete refreshes its dropdown to the
        // empty-input state (generic codes) if it's still open. Without this,
        // the previous prefix-matched results stay visible until the user
        // types again.
        $input.dispatchEvent(new Event('input', { bubbles: true }));
        $input.focus();
    });

    $input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            // Prevent the surrounding #checkout-form from submitting on Enter.
            // The debounced fetchPromos is the source of truth; if it hasn't
            // settled yet, Enter is a no-op rather than a premature submit.
            e.preventDefault();
        }
    });

    return {
        snapshot: () => ({
            promotionalCodeID: applied?.id ?? '',
            code: applied?.code ?? '',
        }),
    };
}
